import { useQuery, type QueryKey } from '@tanstack/react-query';

/**
 * Edit-dialog helper: when a create/edit dialog opens in **edit** mode, refetch the row
 * fresh from its `Get*ById` endpoint so the form seeds from server-truth instead of the
 * (possibly stale) in-memory list row it was handed. On any failure — including the
 * documented "HTTP 200 + business-logic null-ref error" quirk that several `Get*ById`
 * routes return for some accounts' data — it silently falls back to `editing`, so the
 * dialog always has something to seed from and the create path is never touched.
 *
 * Usage: feed the returned row into the dialog's existing `reset(...)` effect in place of
 * the `editing` prop (keep the effect's other deps, add the returned value).
 */
export function useFreshEditRow<TRow>({
  open,
  editing,
  queryKey,
  fetch,
}: {
  open: boolean;
  /** null = create mode → the hook is inert and returns null. */
  editing: TRow | null;
  queryKey: QueryKey;
  fetch: (editing: TRow) => Promise<TRow | undefined>;
}): TRow | null {
  const { data } = useQuery({
    queryKey,
    queryFn: () => fetch(editing as TRow),
    enabled: open && !!editing,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
  return data ?? editing;
}
